import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import {
  SharedTable,
  StatusBadgeConfigType,
  TableEmptyState,
  type SearchInputProps,
} from '@hero/web-components';
import { LoadMainIcon } from '@hero/web-icons';
import { OrderRow } from './OrderRow';
import { OrderTableHead } from './OrderTableHead';
import { OrderTableColGroup } from './orderTableLayout';
import { OrderUnifiedStatus } from '@hero/api';
import { useOrderTableColumns, OrderTableColumn, UseOrderAgainConfig } from '@hero/hooks-shared';

export type { OrderTableColumn };
export interface StatusFilterConfig {
  value: OrderUnifiedStatus | undefined;
  onChange: (value: OrderUnifiedStatus | undefined) => void;
  options: Array<{
    value: OrderUnifiedStatus;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  counts?: Array<{ value: OrderUnifiedStatus; count: number }>;
}

export interface DateFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export interface OrderTableFilters {
  showSearch?: boolean;
  statusFilter?: StatusFilterConfig;
  dateFilter?: DateFilterConfig;
}

export interface OrderTableProps {
  orders: Order[];
  displayForUser: 'customer' | 'carrier';
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  statusConfig?: StatusBadgeConfigType;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  children?: React.ReactNode;

  hasDetailDrawer?: boolean;
  drawerProps?: {
    orderAgainConfig?: UseOrderAgainConfig;
  };

  updateOrderFn?: (data: { _id: string; pod: { podRef?: string } }) => Promise<Order>;

  enableRowSelection?: boolean;
  selectedRowIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;

  columns?: OrderTableColumn[];

  filters?: OrderTableFilters;

  /**
   * Empty-state shown ONLY when the list is empty AND no filters/search are active.
   * Falls back to a translated default per `displayForUser`.
   */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
}

/**
 * OrderTable — renders order rows with optional drawer support.
 *
 * The drawer itself is NOT rendered by the table.
 * Instead, row clicks trigger `useOrderDrawerStore.open(orderId)`.
 * The parent page must render `<OrderDrawer>` somewhere in its tree.
 */
export const OrderTableComponent: React.FC<OrderTableProps> = ({
  orders,
  displayForUser,
  isLoading,
  sortColumn,
  sortDirection,
  searchProps,
  onSort,
  statusConfig,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  children,
  hasDetailDrawer = true,
  drawerProps,
  updateOrderFn,
  enableRowSelection = false,
  selectedRowIds = new Set(),
  onSelectionChange,
  columns: customColumns,
  filters,
  emptyState,
  hasActiveFilters,
}: OrderTableProps) => {
  const { t } = useTranslation(['m-order']);
  const { columns: processedColumns } = useOrderTableColumns({
    displayForUser,
    customColumns,
    includeActions: hasDetailDrawer,
  });

  const defaultEmptyKey = displayForUser === 'customer' ? 'empty_customer' : 'empty_carrier';
  const resolvedEmptyState = emptyState ?? (
    <TableEmptyState
      icon={<LoadMainIcon className="h-7 w-7" />}
      title={t(`m-order:table.${defaultEmptyKey}.title`)}
      description={t(`m-order:table.${defaultEmptyKey}.description`)}
    />
  );

  return (
    <SharedTable
      data={orders}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={statusConfig}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      enableRowSelection={enableRowSelection}
      selectedRowIds={selectedRowIds}
      onSelectionChange={onSelectionChange}
      emptyState={resolvedEmptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(order: Order) => order._id.toString()}>
      {children || <OrderTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={processedColumns}
          tableClassName="table-fixed w-full [&_td]:!py-2.5 [&_td]:text-start [&_th]:h-auto [&_th]:!py-2.5"
          colGroup={
            <OrderTableColGroup columns={processedColumns} hasCheckbox={!!enableRowSelection} />
          }
          enableRowSelection={enableRowSelection}
          rowRender={(order, index) => (
            <OrderRow
              order={order}
              displayForUser={displayForUser}
              hasDetailDrawer={hasDetailDrawer}
              statusConfig={statusConfig}
              columns={processedColumns}
              updateOrderFn={updateOrderFn}
              orderAgainConfig={drawerProps?.orderAgainConfig}
            />
          )}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

export const OrderTable = Object.assign(OrderTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});

export { OrderRow };
